import { useEffect } from "react";
import { getLocalTimeZone } from "@internationalized/date";
import { invariant } from "@pexip-engage/utils";
import { PluginConfiguration } from "@pexip-engage-public/plugin-configuration";
import {
  createSearchParamConfig,
  parseFromSearchParamConfig,
  parsePluginConfiguration,
} from "@pexip-engage-public/plugin-configuration-parser";
import { PluginMisconfigured } from "@pexip-engage-public/plugin-events";
import { ActionFunctionArgs, json, redirect, TypedResponse } from "@remix-run/node";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { Spinner } from "~/components/Loader";
import { i18n } from "~/i18n.server";
import { parseConfigurationToState } from "~/modules/StateManager";
import { ServerStateManager } from "~/modules/StateManager.server";
import { pruneConfiguration } from "~/services/conversion-analytics.utils.server";
import { getMeetingTypes } from "~/services/schedulingRules.server";
import { handleApplicationInitialisation } from "~/utils/application-init.server";
import { getAppointmentData } from "~/utils/appointment.utils.server";
import { validateFlow } from "~/utils/getFlow";
import { LoggerMeta } from "~/utils/logger.server";
import { errors } from "~/utils/validators.utils";

export async function loader(args: ActionFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_plugin_index",
    async function $enterprisePluginIndexLoader(): Promise<TypedResponse<never | null>> {
      const url = new URL(args.request.url);
      const configuration = parseFromSearchParamConfig(
        url.searchParams,
      ) as PluginConfiguration | null;

      if (!configuration) {
        // TODO: add tracking for modern/legacy hit rate: https://skedify.atlassian.net/browse/SKED-9967
        // render page, we probably received a legacy hash :(
        return json(null);
      }

      if (!configuration["language"]) {
        configuration["language"] = await i18n.getLocale(args.request);
      }

      const state = parseConfigurationToState(configuration);
      let stateManager = new ServerStateManager(args, state);

      stateManager.dispatchCAEvent({
        type: "session-config",
        payload: {
          userAgent: args.request.headers.get("user-agent") ?? "",
          configuration: pruneConfiguration(state.config),
        },
      });

      stateManager = await handleApplicationInitialisation(stateManager);

      const { isValid, reason } = validateFlow(stateManager.steps);

      if (!isValid) {
        throw new Error(`Invalid flow: ${reason}`);
      }

      if (state.config.intent === "schedule") {
        // persist manager, so we can access some config values like lng on the thrown response.
        await stateManager.persist();

        // validate before schedule if flow is valid (By doing SRS call)
        const meetingTypes = await getMeetingTypes(stateManager.getSRSParams());
        if (meetingTypes.length === 0) {
          const state = stateManager.pruneDefaults();

          stateManager.logger.info<LoggerMeta>(
            {
              enterprise: stateManager.enterprise,
              extra: {
                config: configuration,
                state,
              },
            },
            "Unschedulable configuration",
          );

          stateManager.throw(
            errors.notSchedulable.create({
              state,
              config: configuration,
            }),
          );
        }
        const firstStep = stateManager.steps[0]?.pathname;
        invariant(firstStep, "No first step defined.");

        return redirect(`./${args.context.sessionId}/schedule/${firstStep}`);
      }

      if (!state.config.oauth.resourceCode) {
        stateManager.throw(
          errors.notFound.create({
            eventType: PluginMisconfigured.INTENT_WITHOUT_RESOURCE_CODE,
            translation: { key: "errors.configuration.missingResourceCode.label" },
          }),
        );
      }

      const appointment = await getAppointmentData(stateManager);
      const appointmentStateManager = stateManager.updateWithAppointment(appointment);

      if (state.config.intent === "invite") {
        const meetingTypes = await getMeetingTypes(appointmentStateManager.getSRSParams());
        if (meetingTypes.length === 0) {
          const state = appointmentStateManager.pruneDefaults();

          appointmentStateManager.logger.info<LoggerMeta>(
            {
              enterprise: stateManager.enterprise,
              extra: {
                state,
                config: configuration,
              },
            },
            "Unschedulable configuration",
          );

          appointmentStateManager.throw(
            errors.notSchedulable.create({
              state,
              config: configuration,
            }),
          );
        }
      }

      await appointmentStateManager.persist();

      return redirect(`./${args.context.sessionId}/appointment/${state.config.intent}`);
    },
  );
}

export default function SessionIndex() {
  useEffect(() => {
    // probably received a hash config, parse and redirect to trigger loader again.
    const config = parsePluginConfiguration({
      hash: window.location.hash,
      browserTimeZone: getLocalTimeZone(),
    }).config;
    const searchParams = createSearchParamConfig({ config });

    // hard reload for sessionId tracking.
    window.location.replace(`?${searchParams.toString()}`);
  }, []);

  return (
    <div className="flex items-center justify-center" style={{ height: "calc(700px - 1rem)" }}>
      <Spinner />
    </div>
  );
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}
